import { HfsSettingsActionEnum } from 'fleet-data-model';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AssetOperationExecuteResponse } from '@fleet-bffs/api-contract';
import type {
  LawnGuidancePayload,
  SearchChargingStationPayload,
  CorridorWidthPayload,
  SingleRangePayload,
  RoboticInstallationSettings,
  GpsNavigationPayload,
} from '~/data/RoboticInstallationSettings';
import { OperationStatus } from '~/domain/assets/models';
import type { AppDispatch } from '~/store/Store';
import type { AppState } from '~/reducers';
import { patchRoboticMowerHfsSettingValue } from '~/api/robotic-mower-api/robotic-mower-api';
import { HfsOperationResult } from '~/domain/assets/models/HfsOperationResult';
import { getHfsInstallationActionSettingDataUpdates } from '~/domain/roboticmower-details/utils/installation-settings-utils';
import { HfsOperationStateReflectionPayload } from '~/domain/assets/models/HfsOperationStateReflectionPayload';

export interface PendingModification {
  code: string;
  payload:
    | LawnGuidancePayload
    | SearchChargingStationPayload
    | CorridorWidthPayload
    | GpsNavigationPayload
    | SingleRangePayload;
}

/* Indicates the lifecycle position of the update record, stored in redux */
export enum UpdateStatus {
  PENDING = 'PENDING',
  CONFIRMED = 'CONFIRMED',
  POSTING = 'POSTING',
  RUNNING = 'RUNNING',
  FAILED = 'FAILED',
}

export interface Update {
  assetId: string;
  status: UpdateStatus;
  payload: PendingModification;
  timestamp?: number;
  operations?: HfsOperationResult[];
}

export const OUTDATED_THRESHOLD = 120000;

export const initialState: Fields = {
  updates: [],
};

export interface Fields {
  assetId?: string;
  settings?: RoboticInstallationSettings;
  updates: Update[];
}

const reducer = createSlice({
  name: 'robotic-installation-settings',
  initialState,
  reducers: {
    init: (
      state,
      action: PayloadAction<{ settings: RoboticInstallationSettings; assetId: string }>
    ) => {
      const { assetId, settings } = action.payload;
      state.assetId = assetId;
      state.settings = settings;
    },
    reset: (state) => {
      state.assetId = undefined;
      state.settings = undefined;
      state.updates = state.updates.filter((item: Update) =>
        [UpdateStatus.RUNNING, UpdateStatus.FAILED].includes(item.status)
      );
    },
    register: (state, action: PayloadAction<PendingModification>) => {
      state.updates.push({
        assetId: state.assetId,
        payload: action.payload,
        status: UpdateStatus.PENDING,
      });
    },
    clearAny: (state, action: PayloadAction<string>) => {
      const code = action.payload;
      state.updates = state.updates.filter(
        (item: Update) => !(item.assetId === state.assetId && item.payload.code === code)
      );
    },
    resetFailed: (state, action: PayloadAction<string>) => {
      const code = action.payload;
      state.updates = state.updates.map((item: Update) =>
        item.assetId === state.assetId && item.payload.code === code
          ? { ...item, status: UpdateStatus.PENDING }
          : item
      );
    },

    /* Marks specific group update as FAILED, in scope of active assetId */
    setFailed: (state, action: PayloadAction<string>) => {
      const code = action.payload;
      state.updates = state.updates.map((item: Update) =>
        item.assetId === state.assetId && item.payload.code === code
          ? { ...item, status: UpdateStatus.FAILED }
          : item
      );
    },

    /* Marks specific group update as CONFIRMED, in scope of active assetId */
    setConfirmed: (state, action: PayloadAction<string>) => {
      const code = action.payload;
      state.updates = state.updates.map((item: Update) =>
        item.assetId === state.assetId && item.payload.code === code
          ? { ...item, status: UpdateStatus.CONFIRMED }
          : item
      );
    },

    setRunningOperations: (
      state,
      action: PayloadAction<{
        code: string;
        operations: AssetOperationExecuteResponse[];
      }>
    ) => {
      const { code, operations } = action.payload;
      state.updates = state.updates.map((item: Update) =>
        item.assetId === state.assetId && item.payload.code === code
          ? { ...item, status: UpdateStatus.RUNNING, timestamp: Date.now(), operations }
          : item
      );
    },

    markOutdatedAsFailed: (
      state,
      action: PayloadAction<{ currentTimestamp: number; assetId: string }>
    ) => {
      const { currentTimestamp, assetId } = action.payload;
      state.updates = state.updates.map((update) =>
        update.assetId === assetId &&
        update.status === UpdateStatus.RUNNING &&
        update.timestamp !== undefined &&
        update.timestamp <= currentTimestamp - OUTDATED_THRESHOLD
          ? { ...update, status: UpdateStatus.FAILED }
          : update
      );
    },

    /* Handler to be called right before POST is being sent to BFF */
    prePost: (state, action: PayloadAction<string[]>) => {
      const codes = action.payload;
      state.updates = state.updates
        .filter(
          (item: Update) =>
            !(
              item.assetId === state.assetId &&
              codes.includes(item.payload.code) &&
              item.status !== UpdateStatus.CONFIRMED
            )
        )
        .map((item: Update) =>
          item.assetId === state.assetId && codes.includes(item.payload.code)
            ? { ...item, status: UpdateStatus.POSTING }
            : item
        );
    },

    /* Temporary form state cleanup function, to be triggered when form is being closed */
    preClose: (state, action: PayloadAction<string[]>) => {
      const codes = action.payload;
      state.updates = state.updates.filter(
        (item: Update) =>
          !(
            item.assetId === state.assetId &&
            codes.includes(item.payload.code) &&
            [UpdateStatus.FAILED, UpdateStatus.PENDING].includes(item.status)
          )
      );
    },

    applyOperationStatusUpdate: (
      state,
      action: PayloadAction<{ operationId: string; status: OperationStatus }>
    ) => {
      const { operationId, status } = action.payload;
      const relevantUpdate = state.updates.find((update) =>
        update?.operations?.some((op) => op.operationId === operationId)
      );
      if (relevantUpdate?.status === UpdateStatus.RUNNING) {
        relevantUpdate.operations = relevantUpdate.operations?.filter(
          (op) => op.operationId !== operationId
        );
        if (status === OperationStatus.Error || status === OperationStatus.Timeout) {
          relevantUpdate.status = UpdateStatus.FAILED;
        }
        if (
          status === OperationStatus.Sent ||
          (status === OperationStatus.Success && relevantUpdate.operations?.length === 0)
        ) {
          // Delete, success
          state.updates = state.updates.filter(
            (item: Update) =>
              !(
                item.assetId === relevantUpdate.assetId &&
                item.payload.code === relevantUpdate.payload.code
              )
          );
        }
        state.updates = state.updates.map((item: Update) =>
          item.assetId === relevantUpdate.assetId &&
          item.payload.code === relevantUpdate.payload.code
            ? relevantUpdate
            : item
        );
      }
    },
  },
});

export const {
  register,
  clearAny,
  markOutdatedAsFailed,
  setConfirmed,
  setFailed,
  resetFailed,
  reset,
  init,
  prePost,
  preClose,
  setRunningOperations,
} = reducer.actions;

export const { applyOperationStatusUpdate } = reducer.actions;

export const reflectOperationStatusUpdate =
  (payload: HfsOperationStateReflectionPayload) =>
  (dispatch: AppDispatch, getState: () => AppState) => {
    const { operationId, status } = payload;
    const { updates } = getState().roboticInstallationSettingsStates;
    const update = updates.find((listItem) =>
      listItem?.operations?.some((op) => op.operationId === operationId)
    );
    if (
      update !== undefined &&
      (status === OperationStatus.Success || status === OperationStatus.Sent)
    ) {
      const operation = update?.operations?.find(
        (listOperation) => listOperation.operationId === operationId
      );
      if (operation?.payload !== undefined) {
        const settingDataUpdates = getHfsInstallationActionSettingDataUpdates(operation);
        if (settingDataUpdates !== undefined) {
          dispatch(
            patchRoboticMowerHfsSettingValue({
              settingDataOverwrites: settingDataUpdates,
              hfsActionId: operation.hfsActionId as unknown as HfsSettingsActionEnum,
              assetId: operation.iprId,
            })
          );
        }
      }
    }
    dispatch(applyOperationStatusUpdate(payload));
  };

export default reducer.reducer;
